// /opt/isp_erp/frontend/src/pages/warehouse/Equipos.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Alert,
  CircularProgress  // ✅ Agregado
} from '@mui/material';
import {
  Search,
  Refresh,
  Visibility,
  History,
  SwapHoriz,
  Delete as DeleteIcon  // ✅ Agregado
} from '@mui/icons-material';
import { warehouseAPI } from '../../api/warehouse.api';
import api from '../../api/axios';
import { useNavigate } from 'react-router-dom';
import { mensajeError } from '../../utils/mensajesError';
import { cargarContextoBodega } from '../../utils/contextoBodega';
import AsignarTecnicoSucursalDialog from '../../components/warehouse/AsignarTecnicoSucursalDialog';
import RevisionEquipoDialog from '../../components/warehouse/RevisionEquipoDialog';
import { RecibirDialog } from '../../components/warehouse/PendientesSucursalPanel';
import { cargarUsuariosDeSucursal } from '../../utils/usuariosSucursal';
import CabeceraBodega from '../../components/warehouse/CabeceraBodega';

const Equipos = () => {
  const navigate = useNavigate();
  const [equipos, setEquipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [busqueda, setBusqueda] = useState('');
  const peticionRef = useRef(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('todos');
  const [tecnicoFilter, setTecnicoFilter] = useState('');
  const [tecnicos, setTecnicos] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedEquipo, setSelectedEquipo] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [ctx, setCtx] = useState(null);
  const [ubicacion, setUbicacion] = useState('');
  const [asignando, setAsignando] = useState(null);
  const [revisando, setRevisando] = useState(null);
  const [recibiendo, setRecibiendo] = useState(null);
  const [deSucursal, setDeSucursal] = useState(new Set());
  useEffect(() => { cargarUsuariosDeSucursal().then(setDeSucursal); }, []);
  const [aviso, setAviso] = useState(null);

  // ✅ Estados para el diálogo de eliminación
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [equipoToDelete, setEquipoToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  useEffect(() => {
    cargarContextoBodega().then((c) => setCtx(c || { central: true, sucursales: [] }));
  }, []);

  useEffect(() => {
    if (ctx) cargarTecnicos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx]);

  // Espera 400 ms después de escribir antes de consultar al servidor
  useEffect(() => {
    const t = setTimeout(() => {
      setBusqueda(searchTerm.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const cargarEquipos = useCallback(async () => {
    const id = ++peticionRef.current;
    setLoading(true);
    try {
      const response = await warehouseAPI.listarEquiposPaginado({
        q: busqueda || undefined,
        estado: estadoFilter !== 'todos' ? estadoFilter : undefined,
        poseedor_tipo: tecnicoFilter !== '' ? 'tecnico' : undefined,
        poseedor_id: tecnicoFilter !== '' ? tecnicoFilter : undefined,
        ubicacion: ubicacion || undefined,
        page: page + 1,
        page_size: rowsPerPage
      });
      if (id !== peticionRef.current) return;
      setEquipos(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      if (id !== peticionRef.current) return;
      console.error('Error cargando equipos:', error);
      setEquipos([]);
      setTotal(0);
    } finally {
      if (id === peticionRef.current) setLoading(false);
    }
  }, [busqueda, estadoFilter, tecnicoFilter, ubicacion, page, rowsPerPage]);

  useEffect(() => {
    cargarEquipos();
  }, [cargarEquipos]);

  // Si se elimina el último equipo de la última página, retrocede una página
  useEffect(() => {
    if (!loading && page > 0 && equipos.length === 0 && total > 0) {
      setPage(Math.max(0, Math.ceil(total / rowsPerPage) - 1));
    }
  }, [loading, page, equipos, total, rowsPerPage]);

  const cargarTecnicos = async () => {
    try {
      if (ctx && ctx.central === false && ctx.sucursal_id) {
        // El encargado solo ve a los técnicos de su sucursal
        const r = await api.get(`/warehouse/sucursales/${ctx.sucursal_id}/personal`);
        setTecnicos((r.data || []).filter((p) => p.rol === 'TECNICO').map((p) => ({ id: p.user_id, username: p.username })));
        return;
      }
      const response = await api.get('/users');
      setTecnicos(response.data || []);
    } catch (error) {
      console.error('Error cargando técnicos:', error);
    }
  };

  const verDetalle = async (equipo) => {
    setSelectedEquipo(equipo);
    try {
      const response = await warehouseAPI.getHistorialEquipo(equipo.sn);
      // 🔍 DEBUG - Puedes quitarlo después de verificar
      console.log('🔍 Historial recibido:', response.data.historial_reciente);
      setHistorial(response.data.historial_reciente || []);
      setDetailOpen(true);
    } catch (error) {
      console.error('Error cargando historial:', error);
    }
  };

  // ✅ Funciones para eliminar
  const handleDeleteClick = (equipo) => {
    setEquipoToDelete(equipo);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    setDeleteError('');
    try {
      await warehouseAPI.eliminarEquipo(equipoToDelete.sn);
      setDeleteSuccess(true);
      setDeleteDialogOpen(false);
      cargarEquipos(); // Recargar la lista

      setTimeout(() => setDeleteSuccess(false), 3000);
    } catch (error) {
      setDeleteError(error.response?.data?.detail || 'Error al eliminar el equipo');
      setTimeout(() => setDeleteError(''), 5000);
    } finally {
      setDeleteLoading(false);
    }
  };

  const puedeOperarSucursal = (sucursalId) => {
    if (!ctx || !ctx.sucursales) return false;
    const s = ctx.sucursales.find((x) => x.id === sucursalId);
    return !!(s && s.puede_operar);
  };

  const devolverACentral = async (equipo) => {
    if (!window.confirm(`¿Devolver ${equipo.sn} a la bodega central?`)) return;
    setAviso(null);
    try {
      const r = await api.post(`/warehouse/sucursales-equipos/${equipo.poseedor.id}/devolver-central`, { equipos: [equipo.sn] });
      setAviso({ tipo: 'success', texto: r.data.mensaje });
      cargarEquipos();
    } catch (err) {
      setAviso({ tipo: 'error', texto: mensajeError(err, 'No se pudo devolver el equipo') });
    }
  };

  const getEstadoColor = (estado) => {
    const colors = {
      BODEGA: 'success',
      EN_TRANSITO: 'warning',
      INSTALADO: 'info',
      RETIRO_PENDIENTE: 'error',
      EN_SUCURSAL: 'secondary',
      RMA_BAJA: 'default'
    };
    return colors[estado] || 'default';
  };

  const getEstadoTexto = (estado) => {
    const textos = {
      BODEGA: 'En Bodega',
      EN_TRANSITO: 'En Tránsito',
      INSTALADO: 'Instalado',
      RETIRO_PENDIENTE: 'Retiro Pendiente',
      EN_SUCURSAL: 'En Sucursal',
      RMA_BAJA: 'RMA/Baja'
    };
    return textos[estado] || estado;
  };

  // ✅ NUEVO: Traducir tipos de movimiento para mostrarlos más amigables
  const getTipoMovimientoTexto = (tipo) => {
    const textos = {
      DESPACHO_BODEGA: 'Despacho a Técnico',
      INSTALACION: 'Instalación en Cliente',
      RETIRO_CAMPO: 'Retiro de Campo',
      RECEPCION_BODEGA: 'Recepción en Bodega',
      TRANSFERENCIA: 'Transferencia',
      RMA: 'RMA',
      REINGRESO: 'Reingreso'
    };
    return textos[tipo] || tipo;
  };

  // Los equipos ya llegan filtrados y paginados desde el servidor
  const paginatedEquipos = equipos;

  return (
    <Box sx={{ p: 3 }}>
      <CabeceraBodega titulo="Gestión de Equipos" subtitulo="Inventario de equipos por estado, técnico y ubicación, con su historial." />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          {ctx && ctx.central === false && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Estás viendo solo los equipos de tu sucursal ({(ctx.sucursales && ctx.sucursales[0] && ctx.sucursales[0].nombre) || ''}), los que vienen en camino y los que tienen en mano tus técnicos.
            </Alert>
          )}
          {ctx && ctx.central !== false && ctx.sucursales && ctx.sucursales.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 260, mb: 2 }}>
              <InputLabel>Ubicación</InputLabel>
              <Select value={ubicacion} label="Ubicación" onChange={(e) => { setUbicacion(e.target.value); setPage(0); }}>
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="central">Bodega central</MenuItem>
                {ctx.sucursales.map((s) => (<MenuItem key={s.id} value={String(s.id)}>{s.nombre}</MenuItem>))}
              </Select>
            </FormControl>
          )}
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Buscar por SN o Modelo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={estadoFilter}
                  onChange={(e) => { setEstadoFilter(e.target.value); setPage(0); }}
                  label="Estado"
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="BODEGA">En Bodega</MenuItem>
                  <MenuItem value="EN_TRANSITO">En Tránsito</MenuItem>
                  <MenuItem value="EN_SUCURSAL">En Sucursal</MenuItem>
                  <MenuItem value="INSTALADO">Instalado</MenuItem>
                  <MenuItem value="RETIRO_PENDIENTE">Retiro Pendiente</MenuItem>
                  <MenuItem value="RMA_BAJA">RMA/Baja</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Técnico</InputLabel>
                <Select
                  value={tecnicoFilter}
                  onChange={(e) => { setTecnicoFilter(e.target.value); setPage(0); }}
                  label="Técnico"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {tecnicos.map((tecnico) => (
                    <MenuItem key={tecnico.id} value={tecnico.id}>
                      {tecnico.username || tecnico.nombre || tecnico.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={cargarEquipos}
                startIcon={<Refresh />}
              >
                Actualizar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {aviso && (
        <Alert severity={aviso.tipo} sx={{ mb: 2 }} onClose={() => setAviso(null)}>{aviso.texto}</Alert>
      )}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>SN</TableCell>
              <TableCell>Modelo</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Poseedor</TableCell>
              <TableCell>Fecha Ingreso</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Cargando...
                </TableCell>
              </TableRow>
            ) : paginatedEquipos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No hay equipos registrados
                </TableCell>
              </TableRow>
            ) : (
              paginatedEquipos.map((equipo) => (
                <TableRow key={equipo.id} hover>
                  <TableCell>
                    <strong>{equipo.sn}</strong>
                  </TableCell>
                  <TableCell>{equipo.modelo}</TableCell>
                  <TableCell>
                    <Chip
                      label={getEstadoTexto(equipo.estado_actual)}
                      color={getEstadoColor(equipo.estado_actual)}
                      size="small"
                    />
                    {equipo.en_revision && (
                      <Chip label="En revisión" color="warning" size="small" sx={{ ml: 1 }} title={equipo.nota_revision || ''} />
                    )}
                  </TableCell>
                  <TableCell>{equipo.poseedor?.nombre || 'Bodega'}</TableCell>
                  <TableCell>
                    {new Date(equipo.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => verDetalle(equipo)}
                      title="Ver detalle e historial"
                    >
                      <History />
                    </IconButton>
                    {equipo.estado_actual === 'BODEGA' && (
                      <>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/warehouse/despachar/${equipo.sn}`)}
                          title="Despachar a técnico"
                        >
                          <SwapHoriz />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(equipo)}
                          title="Eliminar equipo"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                    {equipo.estado_actual === 'EN_TRANSITO' && equipo.poseedor && deSucursal.has(equipo.poseedor.id) && ctx && (ctx.sucursales || []).some((s) => s.puede_operar) && (
                      <Button size="small" color="warning" onClick={() => setRecibiendo({ sn: equipo.sn, modelo: equipo.modelo, tecnico_nombre: equipo.poseedor.nombre })}>Recibir del técnico</Button>
                    )}
                    {equipo.estado_actual === 'EN_SUCURSAL' && equipo.poseedor && equipo.poseedor.tipo === 'Sucursal' && puedeOperarSucursal(equipo.poseedor.id) && (
                      <>
                        {equipo.en_revision ? (
                          <Button size="small" color="secondary" onClick={() => setRevisando(equipo)}>Resolver revisión</Button>
                        ) : (
                          <>
                            <Button size="small" onClick={() => setAsignando(equipo)}>Asignar a técnico</Button>
                            <Button size="small" color="secondary" onClick={() => setRevisando(equipo)}>Poner en revisión</Button>
                          </>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {asignando && (
        <AsignarTecnicoSucursalDialog
          sucursalId={asignando.poseedor.id}
          tipo="equipo"
          item={asignando}
          onClose={() => setAsignando(null)}
          onHecho={(msg) => { setAsignando(null); setAviso({ tipo: 'success', texto: msg }); cargarEquipos(); }}
        />
      )}

      {recibiendo && (
        <RecibirDialog item={recibiendo} onClose={() => setRecibiendo(null)}
          onHecho={(msg) => { setRecibiendo(null); setAviso({ tipo: 'success', texto: msg }); cargarEquipos(); }} />
      )}
      {revisando && (
        <RevisionEquipoDialog
          equipo={revisando}
          onClose={() => setRevisando(null)}
          onHecho={(msg) => { setRevisando(null); setAviso({ tipo: 'success', texto: msg }); cargarEquipos(); }}
        />
      )}

      {/* Dialog de Detalle */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalle del Equipo: {selectedEquipo?.sn}
        </DialogTitle>
        <DialogContent>
          {selectedEquipo && (
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Modelo:</Typography>
                  <Typography variant="body1">{selectedEquipo.modelo}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Estado:</Typography>
                  <Chip
                    label={getEstadoTexto(selectedEquipo.estado_actual)}
                    color={getEstadoColor(selectedEquipo.estado_actual)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Poseedor:</Typography>
                  <Typography variant="body1">
                    {selectedEquipo.poseedor?.nombre || 'Bodega Central'}
                  </Typography>
                </Grid>
                {selectedEquipo.observaciones && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Observaciones:</Typography>
                    <Typography variant="body1">{selectedEquipo.observaciones}</Typography>
                  </Grid>
                )}
              </Grid>

              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                Historial de Movimientos
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Movimiento</TableCell>
                      <TableCell>Origen</TableCell>
                      <TableCell>Destino</TableCell>
                      {/* ✅ NUEVA COLUMNA */}
                      <TableCell>Observaciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historial.length === 0 ? (
                      <TableRow>
                        {/* ✅ Cambiado colSpan de 4 a 5 */}
                        <TableCell colSpan={5} align="center">
                          No hay movimientos registrados
                        </TableCell>
                      </TableRow>
                    ) : (
                      historial.map((mov, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            {new Date(mov.fecha_movimiento).toLocaleString()}
                          </TableCell>
                          {/* ✅ Usar función de traducción (opcional) */}
                          <TableCell>
                            {getTipoMovimientoTexto(mov.tipo_movimiento)}
                          </TableCell>
                          <TableCell>{mov.origen_nombre}</TableCell>
                          <TableCell>{mov.destino_nombre}</TableCell>
                          {/* ✅ NUEVA CELDA CON OBSERVACIONES */}
                          <TableCell>
                            {mov.observaciones ? (
                              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                {mov.observaciones}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Dialog de Confirmación de Eliminación */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleteLoading && setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ bgcolor: '#ffebee', color: '#c62828' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon />
            Confirmar Eliminación
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              ⚠️ Esta acción es irreversible y eliminará permanentemente el equipo.
            </Alert>
            <Typography variant="body1">
              ¿Estás seguro de que deseas eliminar el siguiente equipo?
            </Typography>
            {equipoToDelete && (
              <Paper sx={{ p: 2, mt: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="body2">
                  <strong>SN:</strong> {equipoToDelete.sn}
                </Typography>
                <Typography variant="body2">
                  <strong>Modelo:</strong> {equipoToDelete.modelo}
                </Typography>
                <Typography variant="body2">
                  <strong>Estado:</strong> {getEstadoTexto(equipoToDelete.estado_actual)}
                </Typography>
                <Typography variant="body2">
                  <strong>Fecha de ingreso:</strong> {new Date(equipoToDelete.created_at).toLocaleDateString()}
                </Typography>
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancelar
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleteLoading ? 'Eliminando...' : 'Eliminar Permanentemente'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Snackbar de éxito */}
      {deleteSuccess && (
        <Alert
          severity="success"
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}
          onClose={() => setDeleteSuccess(false)}
        >
          Equipo eliminado correctamente
        </Alert>
      )}

      {/* ✅ Snackbar de error */}
      {deleteError && (
        <Alert
          severity="error"
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}
          onClose={() => setDeleteError('')}
        >
          {deleteError}
        </Alert>
      )}
    </Box>
  );
};

export default Equipos;
